"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import WorkerSection from "@/app/components/WorkerSection";
import AIWorkerCard from "./AIWorkerCard";
import { api, Service } from "@/lib/api";
import { toAbsoluteImageUrl } from "@/lib/imageUtils";
import { useWorkerLayout } from "@/hooks/useWorkerLayout";
import type { LucideIcon } from "lucide-react";
import WorkerAvatar from "./WorkerAvatar";
import Logo from "./Logo";
import { HowAeraWorksSection } from "@/app/components/HowAeraWorksSection";
import {
  CreditsPlanCard,
  CreditsTopupCard,
} from "@/app/components/CreditsPackageCards";
import {
  MessageSquare,
  Coins,
  Sparkles,
  Rocket,
  ShieldCheck,
  Crown,
  Package,
  PackageOpen,
  Boxes,
  Warehouse,
  ListOrdered,
  ChevronDown,
} from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";

const cardClass =
  "rounded-2xl border border-aera-lightgray/90 bg-aera-white p-5 transition-all duration-300";

function FaqContent({
  open,
  children,
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

type UiPlan = {
  name: string;
  credits: string; // number-like string (may contain commas)
  price: string; // number-like string (may contain commas)
  anchor: boolean;
  Icon: LucideIcon;
};

type UiTopup = {
  label: string;
  credits: string; // number-like string (may contain commas)
  price: string; // number-like string (may contain commas)
  Icon: LucideIcon;
};

const FALLBACK_BASE_PLANS: UiPlan[] = [];
const FALLBACK_TOP_UP_BUNDLES: UiTopup[] = [];

const DISCOVER_WORKERS_CACHE_PREFIX = "discover_workers_cache_v1:";
const CREDITS_CATALOG_CACHE_KEY = "credits_catalog_cache_v1";

function safeLocalStorageGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function toNumberLoose(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[,\s฿]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatIntWithCommas(n: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

const PLAN_ICONS = [Sparkles, Rocket, ShieldCheck, Crown] as const;
const TOPUP_ICONS = [Package, PackageOpen, Boxes, Warehouse] as const;

function pickPlanIconByIndex(index: number): LucideIcon {
  if (index < 0) return PLAN_ICONS[0];
  return PLAN_ICONS[Math.min(index, PLAN_ICONS.length - 1)];
}

function pickTopupIconByIndex(index: number): LucideIcon {
  if (index < 0) return TOPUP_ICONS[0];
  return TOPUP_ICONS[Math.min(index, TOPUP_ICONS.length - 1)];
}

function normalizeCreditsCatalogToUi(
  raw: unknown,
): { plans: UiPlan[]; topups: UiTopup[] } | null {
  if (!raw || typeof raw !== "object") return null;
  const r0 = raw as Record<string, unknown>;
  // Common API shape: { success: true, data: { plans: [...], topUps: [...] } }
  const r =
    r0 && typeof r0.data === "object" && r0.data !== null
      ? (r0.data as Record<string, unknown>)
      : r0;

  const maybePlans =
    (r.plans as unknown[]) ||
    (r.monthlyPlans as unknown[]) ||
    (r.planProducts as unknown[]) ||
    (r.planCatalog as unknown[]);
  const maybeTopups =
    (r.topups as unknown[]) ||
    (r.topUps as unknown[]) ||
    (r.topUpBundles as unknown[]) ||
    (r.topupBundles as unknown[]) ||
    (r.bundleProducts as unknown[]) ||
    (r.bundles as unknown[]);

  if (!Array.isArray(maybePlans) && !Array.isArray(maybeTopups)) return null;

  const plans: UiPlan[] = Array.isArray(maybePlans)
    ? maybePlans
        .slice()
        .sort((a, b) => {
          const ao =
            a && typeof a === "object" && a !== null
              ? (toNumberLoose((a as Record<string, unknown>).displayOrder) ??
                0)
              : 0;
          const bo =
            b && typeof b === "object" && b !== null
              ? (toNumberLoose((b as Record<string, unknown>).displayOrder) ??
                0)
              : 0;
          return ao - bo;
        })
        .map((p, index) => {
          if (!p || typeof p !== "object") return null;
          const obj = p as Record<string, unknown>;

          const code =
            (typeof obj.code === "string" && obj.code) ||
            (typeof obj.planCode === "string" && obj.planCode) ||
            "";
          const baseName =
            (obj.name as string) ||
            (obj.nameTh as string) ||
            (obj.title as string) ||
            (obj.label as string) ||
            "";
          const name =
            code && /^(trial|starter|start|standard|pro)$/i.test(code.trim())
              ? code.toLowerCase()
              : baseName;
          const credits =
            toNumberLoose(obj.credits) ??
            toNumberLoose(obj.planCredits) ??
            toNumberLoose(obj.planSize) ??
            toNumberLoose(obj.amount) ??
            null;
          const price =
            toNumberLoose(obj.price) ??
            toNumberLoose(obj.priceMonthly) ??
            toNumberLoose(obj.priceThb) ??
            toNumberLoose(obj.monthlyPrice) ??
            toNumberLoose(obj.priceMonthly) ??
            null;

          if (!name || credits === null || price === null) return null;

          const anchor =
            credits === 400 ||
            name.includes("มาตรฐาน") ||
            name.toLowerCase().includes("standard");

          return {
            name,
            credits: formatIntWithCommas(credits),
            price: formatIntWithCommas(price),
            anchor,
            Icon: pickPlanIconByIndex(index),
          } satisfies UiPlan;
        })
        .filter((x): x is UiPlan => Boolean(x))
    : [];

  const topups: UiTopup[] = Array.isArray(maybeTopups)
    ? maybeTopups
        .slice()
        .sort((a, b) => {
          const ao =
            a && typeof a === "object" && a !== null
              ? (toNumberLoose((a as Record<string, unknown>).displayOrder) ??
                0)
              : 0;
          const bo =
            b && typeof b === "object" && b !== null
              ? (toNumberLoose((b as Record<string, unknown>).displayOrder) ??
                0)
              : 0;
          return ao - bo;
        })
        .map((b, index) => {
          if (!b || typeof b !== "object") return null;
          const obj = b as Record<string, unknown>;

          const label =
            (obj.label as string) ||
            (obj.code as string) ||
            (obj.size as string) ||
            "";
          const credits =
            toNumberLoose(obj.credits) ??
            toNumberLoose(obj.creditAmount) ??
            toNumberLoose(obj.amount) ??
            toNumberLoose(obj.bundleSize) ??
            null;
          const price =
            toNumberLoose(obj.price) ??
            toNumberLoose(obj.priceThb) ??
            toNumberLoose(obj.cost) ??
            null;

          if (!label || credits === null || price === null) return null;

          // Prefer S/M/L/XL if present in label/code
          const shortLabel =
            ["S", "M", "L", "XL"].find((x) => label.toUpperCase() === x) ||
            (() => {
              const code = typeof obj.code === "string" ? obj.code : "";
              return code
                ? (["S", "M", "L", "XL"].find((x) =>
                    code.toUpperCase().includes(x),
                  ) ?? label)
                : label;
            })();

          return {
            label: shortLabel,
            credits: formatIntWithCommas(credits),
            price: formatIntWithCommas(price),
            Icon: pickTopupIconByIndex(index),
          } satisfies UiTopup;
        })
        .filter((x): x is UiTopup => Boolean(x))
    : [];

  if (plans.length === 0 && topups.length === 0) return null;
  return { plans, topups };
}

type CachedCatalog = {
  plans: Array<{
    name: string;
    credits: string;
    price: string;
    anchor: boolean;
  }>;
  topups: Array<{ label: string; credits: string; price: string }>;
};

// Steps moved to `HowAeraWorksSection` to share with the modal.

interface Worker {
  id?: string;
  name: string;
  description: string | string[];
  rating?: string;
  stats?: string;
  thumbnail?: string;
  tags?: string[];
  creatorName?: string;
  creatorImage?: string;
  completedPurchasesCount?: number;
  badgeCount?: number;
}

interface DiscoverContentProps {
  searchQuery?: string;
  isDiscover?: boolean;
  onReadyChange?: (isReady: boolean) => void;
}

export default function DiscoverContent({
  searchQuery = "",
  isDiscover,
  onReadyChange,
}: DiscoverContentProps) {
  const [popularWorkers, setPopularWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [basePlans, setBasePlans] = useState<UiPlan[]>(FALLBACK_BASE_PLANS);
  const [topupBundles, setTopupBundles] = useState<UiTopup[]>(
    FALLBACK_TOP_UP_BUNDLES,
  );
  const [loadingCreditsCatalog, setLoadingCreditsCatalog] = useState(true);
  const [openFaqId, setOpenFaqId] = useState<string | null>("signin");
  const hydratedWorkersRef = useRef(false);
  const hydratedCatalogRef = useRef(false);

  const { isLoggedIn } = useWorkerLayout();

  const workersCacheKey = useMemo(
    () => `${DISCOVER_WORKERS_CACHE_PREFIX}${searchQuery || "__all__"}`,
    [searchQuery],
  );

  const fetchData = useCallback(
    async (search?: string, opts?: { background?: boolean }) => {
      try {
        if (!opts?.background) setLoading(true);
        const servicesResponse = await api.services.list({
          isActive: "true",
          ...(search && { search }),
        });

        let services: Service[] = [];
        if (Array.isArray(servicesResponse)) {
          services = servicesResponse;
        } else if (
          servicesResponse &&
          typeof servicesResponse === "object" &&
          "services" in servicesResponse &&
          Array.isArray((servicesResponse as { services?: unknown }).services)
        ) {
          services = (servicesResponse as { services: Service[] }).services;
        }

        const transformed = services.map((s) => ({
          id: s.id,
          name: s.title,
          description:
            s.description?.split("|||")[0] || "AI assistance for your tasks",
          tags: s.tags?.map((t) => t.value) || ["AI Worker"],
          rating: "★ 0",
          stats: s.packages?.[0]?.price ? `$${s.packages[0].price}` : undefined,
          thumbnail: toAbsoluteImageUrl(s.thumbnail),
          creatorName:
            s.serviceCreator?.displayName ||
            s.creator?.displayName ||
            "AI Worker",
          creatorImage: toAbsoluteImageUrl(
            s.serviceCreator?.avatar || s.creator?.avatar,
          ),
          completedPurchasesCount: s.completedPurchasesCount,
          badgeCount: Math.floor(Math.random() * 20) + 5,
        }));

        // In a real app, these would be separate API calls or filtered
        setPopularWorkers(transformed);

        // Cache last-known-good workers for guest view and post-logout stability.
        safeLocalStorageSet(
          workersCacheKey,
          JSON.stringify({ ts: Date.now(), workers: transformed }),
        );
      } catch (error) {
        console.error("Failed to fetch discover data:", error);
      } finally {
        if (!opts?.background) setLoading(false);
      }
    },
    [workersCacheKey],
  );

  useEffect(() => {
    onReadyChange?.(false);

    // Hydrate workers from cache first (stale-while-revalidate).
    const cached = safeLocalStorageGet(workersCacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as { workers?: Worker[] };
        if (Array.isArray(parsed?.workers) && parsed.workers.length > 0) {
          hydratedWorkersRef.current = true;
          setPopularWorkers(parsed.workers);
          setLoading(false);
          onReadyChange?.(true);
        }
      } catch {
        // ignore cache parse errors
      }
    }

    // Always refetch; do it in background if we already have cached data.
    void fetchData(searchQuery, { background: hydratedWorkersRef.current });
    if (hydratedWorkersRef.current) {
      // In background mode we didn't toggle loading; ensure UI isn't stuck.
      setLoading(false);
      onReadyChange?.(true);
    }
  }, [fetchData, onReadyChange, searchQuery, workersCacheKey]);

  useEffect(() => {
    onReadyChange?.(!loading);
  }, [loading, onReadyChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Hydrate catalog from cache first.
        const cached = safeLocalStorageGet(CREDITS_CATALOG_CACHE_KEY);
        if (cached) {
          try {
            const parsed = JSON.parse(cached) as CachedCatalog;
            if (
              parsed &&
              Array.isArray(parsed.plans) &&
              Array.isArray(parsed.topups) &&
              (parsed.plans.length > 0 || parsed.topups.length > 0)
            ) {
              hydratedCatalogRef.current = true;
              setBasePlans(
                parsed.plans.map((p, i) => ({
                  ...p,
                  Icon: pickPlanIconByIndex(i),
                })),
              );
              setTopupBundles(
                parsed.topups.map((b, i) => ({
                  ...b,
                  Icon: pickTopupIconByIndex(i),
                })),
              );
              setLoadingCreditsCatalog(false);
            }
          } catch {
            // ignore cache parse errors
          }
        }

        setLoadingCreditsCatalog(true);
        const raw = await api.credits.getCatalog();
        const normalized = normalizeCreditsCatalogToUi(raw);

        if (!normalized || cancelled) return;
        setBasePlans(normalized.plans);
        setTopupBundles(normalized.topups);

        // Cache minimal catalog fields (no icons/functions).
        const toCache: CachedCatalog = {
          plans: normalized.plans.map((p) => ({
            name: p.name,
            credits: p.credits,
            price: p.price,
            anchor: p.anchor,
          })),
          topups: normalized.topups.map((b) => ({
            label: b.label,
            credits: b.credits,
            price: b.price,
          })),
        };
        safeLocalStorageSet(
          CREDITS_CATALOG_CACHE_KEY,
          JSON.stringify({ ...toCache, ts: Date.now() }),
        );
      } catch {
        if (cancelled) return;
        // Keep last-known-good (hydrated or previously loaded) instead of clearing.
      } finally {
        if (cancelled) return;
        setLoadingCreditsCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[calc(100dvh-10rem)] w-full items-center justify-center md:min-h-[calc(100dvh-8rem)]">
        <div
          className="h-12 w-12 animate-spin rounded-full border-b-2 border-aera-amber"
          aria-hidden
        />
        <span className="sr-only">กำลังโหลด…</span>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {isLoggedIn ? (
        <>
          {/* คนทำงาน AI ที่นิยมรอบนี้ — temporarily hidden
          {popularWorkers.length > 0 && (
            <WorkerSection
              // title={
              //   "Popular AI Workers This Week"
              // }
              title={"คนทำงาน AI ที่นิยมรอบนี้"}
              workers={popularWorkers}
              renderCard={(worker: Worker) => (
                <AIWorkerCard {...worker} isDiscover={isDiscover} />
              )}
            />
          )}
          {popularWorkers.length === 0 && (
            <div className="text-center text-aera-gray py-12">
              No workers found matching your search.
            </div>
          )}
          */}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-16 md:gap-30 max-w-6xl mx-auto">
          <section
            id="hire-workers"
            className="scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:justify-center md:gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                  <Logo
                    width={20}
                    height={20}
                    classNameCrt="#1F1F1F"
                    classNameStr="#F59E0B"
                    className="h-5 w-5 text-aera-amberdark"
                    aria-hidden
                  />
                </span>
                <h2 className="text-xl font-bold tracking-tight text-aera-charcoal md:text-2xl">
                  {/* Our Workers */}
                  AI Workers ของเรา
                </h2>
              </div>
            </div>

            <div className="w-full flex flex-col gap-10 md:gap-12">
              {popularWorkers.map((worker, index) => {
                const name = worker.name;
                const description = worker.description;
                const tags = worker.tags ?? [];
                const completedPurchasesCount = worker.completedPurchasesCount;
                const ratingLabel = worker.rating;
                const isOdd = (index + 1) % 2 === 1;

                // Guest/marketing area: avoid loading hook-based ratings per card.
                const loadingRating = false;

                const mobileDescription = Array.isArray(description)
                  ? description.slice(0, 3).join(", ")
                  : description;

                return (
                  <div key={worker.id || `${name}-${index}`}>
                    <div
                      className={[
                        "flex w-full flex-col items-center gap-4 text-center",
                        "md:flex-row md:items-start md:gap-6 md:text-left",
                        !isOdd && "md:flex-row-reverse md:text-right",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="relative mx-auto flex h-40 w-40 shrink-0 items-center justify-center overflow-hidden rounded-full bg-aera-lightgray md:mx-0">
                        <WorkerAvatar
                          serviceId={worker.id ?? ""}
                          name={name}
                          thumbnail={worker.thumbnail || worker.creatorImage}
                          size={160}
                          className="h-full w-full shrink-0 rounded-2xl"
                          showInitials={true}
                        />
                      </div>

                      <div className="flex w-full min-w-0 flex-1 flex-col justify-between max-md:items-center">
                        <div className={!isOdd ? "md:text-right" : ""}>
                          <div
                            className={[
                              "flex items-center justify-center",
                              !isOdd ? "md:justify-end" : "md:justify-start",
                            ].join(" ")}
                          >
                            <h3
                              className={[
                                "text-lg font-bold tracking-tight text-aera-charcoal md:text-xl",
                                isOdd ? "md:text-left" : "md:text-right",
                              ].join(" ")}
                              title={name}
                            >
                              {name}
                            </h3>
                          </div>

                          <div
                            className={[
                              "mb-2 flex items-center justify-center gap-2",
                              !isOdd ? "md:justify-end" : "md:justify-start",
                            ].join(" ")}
                          >
                            {completedPurchasesCount !== undefined && (
                              <span className="text-sm text-aera-gray">
                                {completedPurchasesCount} handled
                              </span>
                            )}

                            {loadingRating ? (
                              <span className="text-sm text-aera-gray">
                                <span className="inline-block h-3 w-3 animate-spin rounded-full border border-aera-lightgray/80 border-t-transparent" />
                              </span>
                            ) : (
                              ratingLabel && (
                                <span className="text-sm text-aera-gray">
                                  {ratingLabel}
                                </span>
                              )
                            )}
                          </div>

                          <div
                            className={[
                              "mb-2 flex shrink-0 items-center justify-center gap-2 overflow-hidden",
                              !isOdd ? "md:justify-end" : "md:justify-start",
                            ].join(" ")}
                          >
                            <div
                              className={[
                                "flex justify-center gap-1 overflow-x-auto no-scrollbar mask-linear-fade",
                                !isOdd ? "md:justify-end" : "md:justify-start",
                              ].join(" ")}
                            >
                              {tags.length > 0
                                ? tags.slice(0, 3).map((tag, i) => (
                                    <span
                                      key={i}
                                      className="whitespace-nowrap rounded bg-aera-offwhite px-2 py-0.5 text-[10px] text-aera-gray"
                                    >
                                      {tag}
                                    </span>
                                  ))
                                : [1, 2, 3].map((i) => (
                                    <div
                                      key={i}
                                      className="h-3 w-8 rounded bg-aera-offwhite"
                                    />
                                  ))}
                              {tags.length > 3 && (
                                <span className="whitespace-nowrap px-1 py-0.5 text-[10px] text-aera-gray">
                                  +{tags.length - 3}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="max-md:max-h-none max-h-[40px]">
                            {Array.isArray(description) ? (
                              <>
                                <p className="line-clamp-3 text-center text-sm leading-relaxed text-aera-gray md:hidden">
                                  {mobileDescription}
                                </p>
                                <ul className="hidden space-y-1 md:block">
                                  {description.slice(0, 3).map((item, i) => (
                                    <li
                                      key={i}
                                      className={[
                                        "flex items-start gap-1.5 align-top",
                                        !isOdd &&
                                          "flex-row-reverse justify-end",
                                      ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    >
                                      <svg
                                        className="mt-0.5 h-4 w-4 shrink-0 text-aera-amber"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                      <span
                                        className={[
                                          "line-clamp-1 text-sm leading-tight text-aera-gray",
                                          !isOdd && "text-right",
                                        ]
                                          .filter(Boolean)
                                          .join(" ")}
                                      >
                                        {item}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <p
                                className={[
                                  "line-clamp-2 text-center text-sm leading-relaxed text-aera-gray",
                                  !isOdd ? "md:text-right" : "md:text-left",
                                ].join(" ")}
                              >
                                {description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div id="hire-how-it-works">
            <HowAeraWorksSection
              className="scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
              showTitleIcon
              titleIcon={
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                  <ListOrdered
                    className="h-5 w-5 text-aera-amberdark"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                </span>
              }
              showCta
              ctaHref="/signin"
            />
          </div>

          <section
            id="hire-benefits"
            className="scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:justify-center md:gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                  <Sparkles
                    className="h-5 w-5 text-aera-amberdark"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                </span>
                <h2 className="text-xl font-bold tracking-tight text-aera-charcoal md:text-2xl">
                  ข้อดีของ AI Workers บน Aera
                </h2>
              </div>
              <p className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-aera-gray">
                ทำงานได้ไวขึ้น คุมงานได้ง่ายขึ้น และขยายทีมได้เมื่อพร้อม —
                ทั้งหมดอยู่ในที่เดียว
              </p>
            </div>

            <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-2xl bg-aera-offwhite p-6">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                    <span className="text-aera-amberdark font-bold">1</span>
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-aera-charcoal">
                      เร็วและสม่ำเสมอ
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                      ให้คนทำงานซ้ำ ๆ ไปทำงานแทนคุณ ลดเวลารอ
                      และได้ผลลัพธ์มาตรฐานทุกครั้ง
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-aera-offwhite p-6">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                    <span className="text-aera-amberdark font-bold">2</span>
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-aera-charcoal">
                      คุมงานได้ชัดเจน
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                      มีบริบทงาน ประวัติ และขั้นตอนให้ตรวจสอบได้
                      ลดงานตกหล่นและช่วยให้ส่งมอบได้ตรงเป้า
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-aera-offwhite p-6">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                    <span className="text-aera-amberdark font-bold">3</span>
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-aera-charcoal">
                      ขยายทีมได้ทันที
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-aera-gray">
                      เลือก worker ให้เหมาะกับงาน เพิ่ม/ลดกำลังคนตามช่วงเวลา
                      โดยไม่ต้องเสีย overhead การจ้างงาน
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            id="hire-credits"
            className="scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex flex-col items-center gap-2 text-center md:flex-row md:items-center md:justify-center md:gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/25">
                  <Coins
                    className="h-5 w-5 text-aera-amberdark"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                </span>
                <h2 className="text-xl font-bold tracking-tight text-aera-charcoal md:text-2xl">
                  เครดิต & แพ็กเกจ
                </h2>
              </div>
            </div>

            <div className="w-full space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <h3 className="text-lg text-center font-bold tracking-tight text-aera-charcoal md:text-xl">
                  แพ็กเกจรายเดือน
                </h3>
                <p className="mx-auto text-center text-sm leading-relaxed text-aera-gray">
                  แพ็กเกจหลัก —
                  เครดิตจะรีเซ็ตตามจำนวนของแพ็กเกจในวันต่ออายุรายเดือน
                </p>
              </div>
              {loadingCreditsCatalog ? (
                <div className="text-center text-sm text-aera-gray py-6">
                  กำลังโหลดแพ็กเกจ...
                </div>
              ) : basePlans.length === 0 ? (
                <div className="text-center text-sm text-aera-gray py-6">
                  ไม่พบแพ็กเกจรายเดือนในขณะนี้
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  {basePlans.map(({ name, credits, price, anchor, Icon }) => (
                    <CreditsPlanCard
                      key={name}
                      title={name}
                      credits={credits}
                      price={price}
                      Icon={Icon}
                      selected={anchor}
                      badgeLabel={anchor ? "แนะนำ" : undefined}
                      showPerMonth
                      fixedWidth={false}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="h-px w-full bg-linear-to-r from-transparent via-aera-lightgray to-transparent" />

            <div className="w-full space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <h3 className="text-lg text-center font-bold tracking-tight text-aera-charcoal md:text-xl">
                  แพ็กเกจเติม
                </h3>
                <p className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-aera-gray">
                  บันเดิลเติม — ไม่มีวันหมดอายุ
                  และสามารถเติมทบเพิ่มจากแพ็กเกจหลักได้
                </p>
              </div>

              {loadingCreditsCatalog ? (
                <div className="text-center text-sm text-aera-gray py-6">
                  กำลังโหลดแพ็กเกจ...
                </div>
              ) : topupBundles.length === 0 ? (
                <div className="text-center text-sm text-aera-gray py-6">
                  ไม่พบบันเดิลเติมเครดิตในขณะนี้
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                  {topupBundles.map(({ label, credits, price, Icon }) => (
                    <CreditsTopupCard
                      key={label}
                      label={label}
                      credits={credits}
                      price={price}
                      Icon={Icon}
                      fixedWidth={false}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section
            id="hire-faq"
            className="scroll-mt-6 w-full flex flex-col items-center justify-center gap-8"
          >
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:justify-center md:gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight/35 ring-1 ring-aera-amber/20">
                  <MessageSquare
                    className="h-5 w-5 text-aera-amberdark"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                </span>
                <h2 className="text-xl font-bold tracking-tight text-aera-charcoal md:text-2xl">
                  คำถามที่พบบ่อย
                </h2>
              </div>
              <p className="mx-auto max-w-3xl text-center text-sm leading-relaxed text-aera-gray">
                คำตอบสั้น ๆ สำหรับคำถามที่เจอบ่อยที่สุด
              </p>
            </div>

            <div className="w-full space-y-3">
              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "signin" ? null : "signin",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "signin"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "signin" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    ทำไมต้อง Sign in ด้วย Google / LINE?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "signin"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      การ Sign in ด้วย Google/LINE ช่วยให้ยืนยันตัวตนได้รวดเร็ว
                      โดยไม่ต้องตั้งรหัสผ่านใหม่
                      ลดความเสี่ยงจากรหัสผ่านรั่ว/เดาง่าย
                      และช่วยป้องกันบัญชีปลอม หรือการใช้งานผิดวัตถุประสงค์
                      เราจะใช้ข้อมูลเท่าที่จำเป็นสำหรับ “การเข้าสู่ระบบ”
                      เท่านั้น (เช่น
                      ไอดีผู้ใช้/อีเมล/ชื่อโปรไฟล์ตามที่ผู้ให้บริการอนุญาต)
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          สะดวก
                        </span>
                        : เข้าสู่ระบบได้ในไม่กี่คลิก ไม่ต้องจำรหัสผ่านเพิ่ม
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ปลอดภัย
                        </span>
                        : ใช้มาตรฐานความปลอดภัยของ Google/LINE และสามารถเปิด 2FA
                        ได้
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ต่อเนื่อง
                        </span>
                        :
                        สามารถกลับมาใช้งาน/ดูประวัติงานและเครดิตได้จากบัญชีเดิม
                      </li>
                    </ul>
                    <div className="rounded-xl bg-aera-amberlight/25 p-3 ring-1 ring-aera-amber/15">
                      <p className="font-medium text-aera-charcoal">
                        เคล็ดลับให้ Verify Google ผ่านง่าย
                      </p>
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        <li>
                          แนะนำใช้ Chrome/Safari เวอร์ชันล่าสุด และปิดโหมด
                          Incognito ถ้ามีปัญหา
                        </li>
                        <li>อนุญาต Pop-up/Redirect ชั่วคราวสำหรับหน้า Login</li>
                        <li>
                          ถ้าเลือกบัญชีผิด ให้ “Sign out” จากบัญชีอื่นก่อน
                          แล้วลองใหม่
                        </li>
                        <li>
                          ถ้าติดวนหน้าเดิม ให้ล้าง cache/cookies ของโดเมนนี้
                          แล้วลองอีกครั้ง
                        </li>
                      </ul>
                    </div>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "features" ? null : "features",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "features"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "features" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    สมัครแล้วทำอะไรได้บ้าง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "features"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      หลังสมัคร/เข้าสู่ระบบ คุณจะใช้งานได้ครบในบัญชีเดียว
                      ตั้งแต่ค้นหา worker ไปจนถึงติดตามงานและจัดการเครดิต
                      เพื่อให้การทำงานต่อเนื่องและตรวจสอบย้อนหลังได้
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ค้นหา & ทดลองคุย
                        </span>
                        : ทดลองถาม-ตอบเพื่อดูแนวทางการทำงานก่อนตัดสินใจจ้าง
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          จ้างงาน
                        </span>
                        : เลือกแพ็กเกจ/จำนวนเครดิต แล้วเริ่มงานได้ทันที
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ติดตามสถานะ
                        </span>
                        : ดูความคืบหน้า/ประวัติแชท และส่ง feedback เพื่อปรับงาน
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          จัดการเครดิต/แพ็กเกจ
                        </span>
                        : เปลี่ยนแพ็กเกจรายเดือน หรือเติมเครดิตตามช่วงที่ใช้งาน
                      </li>
                    </ul>
                    <p className="text-xs text-aera-gray">
                      หมายเหตุ: ฟีเจอร์บางอย่างอาจขึ้นกับประเภท worker
                      และแพ็กเกจที่เลือก
                    </p>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "privacy" ? null : "privacy",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "privacy"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "privacy" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    ข้อมูลของฉันปลอดภัยไหม?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "privacy"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      เราออกแบบระบบให้ใช้ข้อมูลเท่าที่จำเป็นต่อการให้บริการ
                      และพยายามลดการเก็บข้อมูลที่ไม่เกี่ยวข้อง
                      เพื่อให้คุณใช้งานได้อย่างสบายใจ
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ข้อมูลบัญชี
                        </span>
                        : ใช้เพื่อยืนยันตัวตน/สร้างเซสชันการใช้งาน
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ข้อมูลการใช้งาน
                        </span>
                        : ใช้เพื่อแสดงประวัติการใช้งาน การจ้างงาน
                        และเครดิตอย่างถูกต้อง
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ไฟล์ที่แนบ
                        </span>
                        : ใช้เพื่อให้ worker เข้าใจบริบทงาน
                        (และจะแสดงเฉพาะในบริบทที่คุณใช้งาน)
                      </li>
                    </ul>
                    <p>
                      คุณสามารถดูรายละเอียดเพิ่มเติมได้ในหน้า{" "}
                      <span className="font-medium text-aera-charcoal">
                        Privacy/Terms
                      </span>
                      รวมถึงแนวทางการใช้ข้อมูลและเงื่อนไขการใช้งาน
                    </p>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "credits" ? null : "credits",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "credits"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "credits" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    เครดิตคืออะไร และใช้ยังไง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "credits"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      เครดิตคือ “หน่วยการใช้งาน” สำหรับเรียกใช้ worker
                      หรือฟีเจอร์บางอย่าง
                      โดยระบบจะแจ้งการใช้เครดิตให้เห็นก่อนกดยืนยันเสมอ
                      เพื่อให้คุณควบคุมค่าใช้จ่ายได้
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          แพ็กเกจรายเดือน
                        </span>
                        : เหมาะกับการใช้งานต่อเนื่อง
                        เครดิตจะรีเซ็ตตามจำนวนแพ็กเกจเมื่อถึงรอบต่ออายุ
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          แพ็กเกจเติม
                        </span>
                        : เหมาะกับช่วงที่ต้องการใช้งานเพิ่ม
                        บันเดิลเติมไม่มีวันหมดอายุและเติมทบได้
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ก่อนยืนยัน
                        </span>
                        :
                        ระบบจะแสดงจำนวนเครดิตที่ต้องใช้และสรุปการทำรายการก่อนเสมอ
                      </li>
                    </ul>
                    <p className="text-xs text-aera-gray">
                      แนะนำ: ถ้าเพิ่งเริ่ม ลองแพ็กเกจรายเดือนก่อน
                      แล้วค่อยเติมตามช่วงที่มีงานเยอะ
                    </p>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) => (prev === "hire" ? null : "hire"))
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "hire"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "hire" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    จ้าง worker ต้องทำอะไรบ้าง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "hire"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      ขั้นตอนการจ้าง worker โดยรวมจะเป็นลำดับง่าย ๆ
                      และทำได้ในแชทเดียว
                      เพื่อให้ส่งงาน-แก้งานสะดวกและไม่หลุดบริบท
                    </p>
                    <ol className="list-decimal space-y-1 pl-5">
                      <li>
                        เลือก worker ที่เหมาะกับงาน (ดู Best for / สิ่งที่ทำได้
                        / ตัวอย่างงาน)
                      </li>
                      <li>กดจ้าง และเลือกแพ็กเกจ/เครดิตที่ต้องใช้</li>
                      <li>
                        ระบุโจทย์ให้ชัด (เป้าหมาย, รูปแบบผลลัพธ์, ข้อจำกัด,
                        เดดไลน์)
                      </li>
                      <li>
                        แนบไฟล์/ข้อมูลที่จำเป็น (เช่น รูป, เอกสาร, ไฟล์ข้อมูล,
                        ลิงก์)
                      </li>
                      <li>
                        ติดตามสถานะและให้ feedback
                        เพื่อปรับแก้จนได้ผลลัพธ์ที่ต้องการ
                      </li>
                    </ol>
                    <div className="rounded-xl bg-aera-offwhite p-3 ring-1 ring-aera-lightgray/80">
                      <p className="font-medium text-aera-charcoal">
                        ตัวอย่างโจทย์ที่ดี
                      </p>
                      <p className="mt-1 text-sm text-aera-gray">
                        “ช่วยสรุปเอกสาร 1 หน้าเป็น bullet 8–10 ข้อ และทำ action
                        items แยกท้ายสุด”
                      </p>
                    </div>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) => (prev === "files" ? null : "files"))
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "files"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "files" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    อัปโหลดไฟล์อะไรได้บ้าง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "files"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      โดยทั่วไปคุณสามารถแนบไฟล์ที่เกี่ยวกับงานเพื่อให้ worker
                      เข้าใจบริบทได้เร็วขึ้น เช่น รูป, เอกสาร, หรือไฟล์ข้อมูล
                      (ถ้าเป็นลิงก์ก็สามารถส่งเป็นข้อความได้เช่นกัน)
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        รูปภาพ/สกรีนช็อต:
                        อธิบายสิ่งที่ต้องแก้/สิ่งที่ต้องการให้ได้
                      </li>
                      <li>
                        เอกสาร/ไฟล์งาน: ระบุหน้าหรือหัวข้อที่ต้องการให้โฟกัส
                      </li>
                      <li>
                        ไฟล์ข้อมูล: บอกคอลัมน์สำคัญและรูปแบบผลลัพธ์ที่ต้องการ
                      </li>
                    </ul>
                    <p className="text-xs text-aera-gray">
                      ถ้าอัปโหลดไม่ได้ แนะนำให้ลองเปลี่ยนชื่อไฟล์ให้สั้นลง,
                      หลีกเลี่ยงอักขระพิเศษ, ลดขนาดไฟล์ หรืออัปโหลดใหม่อีกครั้ง
                    </p>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "billing" ? null : "billing",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "billing"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "billing" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    แพ็กเกจ/การคิดเงินทำงานยังไง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "billing"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      ระบบคิดเงินของ Aera แบ่งง่าย ๆ เป็น 2 แบบ:{" "}
                      <span className="font-medium text-aera-charcoal">
                        แพ็กเกจรายเดือน
                      </span>{" "}
                      และ{" "}
                      <span className="font-medium text-aera-charcoal">
                        แพ็กเกจเติม
                      </span>
                      เพื่อให้คุณเลือกได้ตามพฤติกรรมการใช้งาน
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          รายเดือน
                        </span>
                        : เหมาะกับการใช้ต่อเนื่อง
                        เครดิตจะรีเซ็ตเมื่อถึงรอบต่ออายุของแพ็กเกจ
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          เติม
                        </span>
                        : เหมาะกับช่วงที่มีงานพีค บันเดิลเติมสามารถเติมทบเพิ่ม
                        และไม่มีวันหมดอายุ
                      </li>
                      <li>
                        <span className="font-medium text-aera-charcoal">
                          ความชัดเจนก่อนจ่าย
                        </span>
                        : ราคาและจำนวนเครดิตจะแสดงให้เห็นก่อนทำรายการทุกครั้ง
                      </li>
                    </ul>
                    <p className="text-xs text-aera-gray">
                      แนะนำ: ถ้าต้องการคุมงบ ลองเริ่มด้วยรายเดือน
                      แล้วใช้แพ็กเติมเฉพาะช่วงที่ต้องการเร่งงาน
                    </p>
                  </div>
                </FaqContent>
              </div>

              <div className={cardClass}>
                <AeraButton
                  variant="ghost"
                  fullWidth
                  type="button"
                  onClick={() =>
                    setOpenFaqId((prev) =>
                      prev === "support" ? null : "support",
                    )
                  }
                  paddingSize="none"
                  fontWeight="semibold"
                  aria-expanded={openFaqId === "support"}
                  iconRight={
                    <ChevronDown
                      size={18}
                      className={` text-aera-gray opacity-100 transition-all duration-200 ease-out ${
                        openFaqId === "support" ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  }
                >
                  <span className="text-left w-full">
                    มีปัญหาในการล็อกอิน/ยืนยันตัวตน ต้องทำยังไง?
                  </span>
                </AeraButton>
                <FaqContent open={openFaqId === "support"}>
                  <div className="mt-3 space-y-2 text-sm leading-relaxed text-aera-gray">
                    <p>
                      ถ้าล็อกอิน/ยืนยันตัวตนไม่ผ่าน ส่วนใหญ่เกิดจากการบล็อก
                      pop-up/redirect, cache ค้าง, หรือเลือกบัญชีผิด
                      ลองทำตามขั้นตอนนี้ทีละข้อ
                    </p>
                    <ol className="list-decimal space-y-1 pl-5">
                      <li>รีเฟรชหน้า แล้วลองเข้าสู่ระบบใหม่</li>
                      <li>อนุญาต pop-up/redirect ชั่วคราวสำหรับหน้า Login</li>
                      <li>ปิดโหมด Incognito/Private (ถ้าเปิดอยู่)</li>
                      <li>ล้าง cache/cookies ของโดเมนนี้ แล้วลองอีกครั้ง</li>
                      <li>
                        ลองเปลี่ยนเบราว์เซอร์หรืออุปกรณ์ (Chrome/Safari
                        เวอร์ชันล่าสุด)
                      </li>
                    </ol>
                    <p className="text-xs text-aera-gray">
                      หากยังไม่ผ่าน{" "}
                      <AeraButton
                        as="a"
                        href="/support/new"
                        variant="link"
                        className="inline text-aera-amber hover:text-aera-charcoal"
                      >
                        ส่งตั๋วแจ้งปัญหา
                      </AeraButton>{" "}
                      หรือดูกระทู้สาธารณะที่{" "}
                      <AeraButton
                        as="a"
                        href="/support"
                        variant="link"
                        className="inline text-aera-amber hover:text-aera-charcoal"
                      >
                        บอร์ดช่วยเหลือ
                      </AeraButton>{" "}
                      พร้อมภาพหน้าจอ ข้อความ error และเวลาที่เกิดปัญหา
                    </p>
                  </div>
                </FaqContent>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
